import {
  InputChangeEventDetail,
  IonButton,
  IonIcon,
  IonInput,
  useIonLoading,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import AuthHelper from 'lib/auth.common';
import { Auth } from 'aws-amplify';
import { eyeOutline, eyeOffOutline, lockClosedOutline, warningOutline } from 'ionicons/icons';
import * as yup from 'yup';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { onPromise } from '../../CreateAccount';
import { IonInputCustomEvent } from '@ionic/core';
import { ReduxCommandCreator } from 'appRedux/actions';
import { useDispatch } from 'react-redux';
import { AuthService, IUserInfo } from 'services/auth';
import { AUTH_STATE } from 'appRedux/types/reduxTypes';
import { CHALLENGE_PENDING_DISMISS_KEY } from 'common/sessionKeys';
import { beginApplicationConfigLoginSession } from 'lib/applicationConfig';
import { markResumeLastViewedCasino } from 'lib/lastViewedCasino';
import { clearSession, startSession } from 'lib/sessionExpiry';
import { MobileNumberField } from '../../../components/MobileNumberField';
import { useSupportedPhoneCountries } from 'common/reactHooks';
import {
  formatPhoneNumberForCountry,
  getDefaultSupportedPhoneCountryId,
  getInternationalPhoneNumber,
  getPhoneDisplayValue,
  getPhoneInputMaxLength,
  getSupportedPhoneCountry,
  isPhoneNumberValidForCountry,
  SupportedPhoneCountryId,
} from '../../../../../common/phoneCountries';

interface FormData {
  password: string;
}

export const login_schema = yup.object().shape({
  password: yup
    .string()
    .min(8, 'Password must include at least 8 characters')
    .required('Password is required'),
});

interface IAWSAuthUser {
  signInUserSession: {
    accessToken: {
      payload: {
        'cognito:groups': Array<string>;
      };
    };
  };
}

export const SignInAccount = () => {
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const [showPasswordNew, setShowPasswordNew] = useState(false);

  const [presentToast] = useIonToast();
  const [present, dismiss] = useIonLoading();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(login_schema),
  });
  const [phone, setPhone] = useState('');
  const [phoneErr, setPhoneErr] = useState('');
  const [selectedCountryId, setSelectedCountryId] = useState<SupportedPhoneCountryId | undefined>();
  const router = useIonRouter();
  const { supportedPhoneCountries } = useSupportedPhoneCountries();
  const selectedCountry = getSupportedPhoneCountry(selectedCountryId, supportedPhoneCountries);

  useEffect(() => {
    const defaultCountryId = getDefaultSupportedPhoneCountryId(supportedPhoneCountries);
    const hasSelectedCountry = supportedPhoneCountries.some(
      (country) => country.shortLabel === selectedCountryId
    );

    if ((!selectedCountryId || !hasSelectedCountry) && defaultCountryId) {
      setSelectedCountryId(defaultCountryId);
    }
  }, [selectedCountryId, supportedPhoneCountries]);

  const onSubmit = async (data: FormData) => {
    if (!selectedCountry) {
      setPhoneErr('Mobile country options are loading');
      return;
    }
    if (!phone) {
      setPhoneErr('Cell number is required');
      return;
    } else {
      setPhoneErr('');
    }
    const validatePhNo = isPhoneNumberValidForCountry(phone, selectedCountry);
    if (!validatePhNo) {
      setPhoneErr(`Please enter a valid ${selectedCountry.label.toLowerCase()} mobile number`);
      return;
    }

    await present({
      message: 'Loading...',
      cssClass: 'custom-loading',
    });

    try {
      const internationalPhoneNumber = getInternationalPhoneNumber(phone, selectedCountry);
      const phoneDisplayValue = getPhoneDisplayValue(phone, selectedCountry);
      const username = `+${internationalPhoneNumber}`;
      const password = data.password;
      const user = (await Auth.signIn(username, password)) as IAWSAuthUser;
      // A new login replaces any session record a previous user left behind, so the
      // inactivity check never logs this sign-in out before Login returns.
      clearSession();
      const service = new AuthService();
      service.Login().subscribe({
        next(loginResponse) {
          // New login = new session: refetch applicationConfigurations; show
          // Personal Invited Challenges again even if dismissed last session.
          beginApplicationConfigLoginSession();
          commands.SetAuthState(AUTH_STATE.AUTHENTICATED);
          try {
            sessionStorage.removeItem(CHALLENGE_PENDING_DISMISS_KEY);
          } catch {
            /* sessionStorage unavailable — nothing to reset */
          }
          let groups: string[] = [];
          if (user && user.signInUserSession?.accessToken?.payload['cognito:groups']) {
            groups = user.signInUserSession.accessToken.payload['cognito:groups'];
          }

          if (loginResponse?.response) {
            const {
              emailAddress,
              aliasInitial,
              aliasInitial2,
              aliasName,
              appUserPk,
              firstName,
              lastName,
              isCasinoAccount,
              showLoadingReels,
              slotCheckSearchEnabled,
            } = loginResponse.response.user;

            AuthHelper.SetToken(loginResponse.response.token as string);
            // Inactivity logout: 4h with a verified Player ID link at any casino, else 1h.
            startSession(Date.now(), loginResponse.response.user?.linkedAccounts);
            const lastLogin = loginResponse?.response?.lastLogin;
            const last_login_timestamp_counter = lastLogin?.last_login_timestamp_counter;
            const shoUserConfirm: boolean =
              !last_login_timestamp_counter || last_login_timestamp_counter < 1 ? true : false;

            const aliasInformation: Partial<IUserInfo> = {
              aliasInitial: aliasInitial,
              aliasInitial2: aliasInitial2,
              aliasName: aliasName,
            };

            commands.InitUserInfo(
              internationalPhoneNumber,
              phoneDisplayValue,
              emailAddress as string,
              appUserPk,
              groups
            );
            commands.SetTourState(loginResponse.response.user?.hasCompletedOnboarding as boolean);
            commands.SetUserAlias({
              ...aliasInformation,
            });
            const userInformation: Partial<IUserInfo> = {
              firstName: firstName,
              lastName: lastName,
              emailAddress: emailAddress,
              isCasinoAccount,
              // Opt-in Explore slot-reel loading cards (users.showLoadingReels).
              showLoadingReels: showLoadingReels ?? null,
              // Slot Check Search access (users.slotCheckSearchEnabled).
              slotCheckSearchEnabled: slotCheckSearchEnabled ?? null,
            };

            commands.SetUserInformation({
              ...userInformation,
            });
            commands.SetUserConfirm(shoUserConfirm);
            // Resume last-viewed casino on ChooseCasinos (flag survives userconfirm).
            markResumeLastViewedCasino();
            if (shoUserConfirm) {
              router.push('/auth/userconfirm');
            } else {
              router.push('/auth/choosecasinos');
            }
          }

          dismiss()
            .then(() => {
              return;
            })
            .catch(() => {
              return;
            });
        },
        error() {
          void presentToast({
            position: 'top',
            duration: 5000,
            color: 'danger',
            message: 'Login has failed, please try again if this persists please contact support.',
          });
          dismiss()
            .then(() => {
              return;
            })
            .catch(() => {
              return;
            });
        },
      });
    } catch (error) {
      let message = 'Unknown Error';
      if (error instanceof Error) message = error.message;
      const internationalPhoneNumber = getInternationalPhoneNumber(phone, selectedCountry);
      const phoneDisplayValue = getPhoneDisplayValue(phone, selectedCountry);
      const username = `+${internationalPhoneNumber}`;
      switch (message) {
        case 'User is not confirmed.':
          message = 'Sorry, User is not confirmed. Please verify your cell number and confirm.';
          await Auth.resendSignUp(username);
          commands.InitUserInfo(internationalPhoneNumber, phoneDisplayValue);
          router.push('/auth/verifyaccount');
          break;
        case 'Incorrect username or password.':
          message = 'Sorry, Incorrect password.';
          break;
        case 'User does not exist.':
          message = 'Sorry, User does not exist.';
          break;
        default:
          if (error instanceof Error) message = error.message;
          break;
      }
      void presentToast({
        position: 'top',
        duration: 5000,
        color: 'danger',
        message: message,
      });

      dismiss()
        .then(() => {
          return;
        })
        .catch(() => {
          return;
        });
    }
  };

  const inputEl = useRef<HTMLIonInputElement>(null);

  const formatPhone = (ev: IonInputCustomEvent<InputChangeEventDetail>) => {
    if (!selectedCountry) {
      return;
    }
    const value = `${ev.target.value ?? ''}`;
    const filteredValue = formatPhoneNumberForCountry(value, selectedCountry);
    setPhone(filteredValue);

    const inputCmp = inputEl.current;
    if (inputCmp !== null) {
      inputCmp.value = filteredValue;
    }
  };

  const onCountryChange = (countryId: SupportedPhoneCountryId) => {
    const nextCountry = getSupportedPhoneCountry(countryId, supportedPhoneCountries);
    if (!nextCountry) {
      return;
    }
    setSelectedCountryId(countryId);
    setPhoneErr('');

    const reformattedPhone = formatPhoneNumberForCountry(phone, nextCountry);
    setPhone(reformattedPhone);

    const inputCmp = inputEl.current;
    if (inputCmp !== null) {
      inputCmp.value = reformattedPhone;
    }
  };

  return (
    <div className="login-v2-form">
      <form
        id="confirm-page"
        onSubmit={onPromise(handleSubmit((data: FormData) => void onSubmit(data)))}
      >
        {/* Phone field */}
        <div className="login-v2-field-wrap login-v2-phone-wrap">
          <div className="login-v2-phone-content">
            <MobileNumberField
              value={phone}
              onChange={formatPhone}
              maxLength={selectedCountry ? getPhoneInputMaxLength(selectedCountry) : 0}
              placeholder="Enter phone number"
              inputId="username-field"
              inputRef={inputEl}
              selectedCountryId={selectedCountryId}
              selectedCountryDialCode={selectedCountry?.dialCode}
              onCountryChange={onCountryChange}
              countryOptions={supportedPhoneCountries}
              showClear
              onClear={() => {
                setPhone('');
                if (inputEl.current) inputEl.current.value = '';
              }}
            />
          </div>
        </div>
        {phoneErr && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{phoneErr}</span>
          </div>
        )}

        {/* Password field */}
        <div className="login-v2-field-wrap login-v2-pass-wrap">
          <IonIcon
            icon={lockClosedOutline}
            className="login-v2-field-prefix-icon login-v2-field-prefix-icon--mid"
          />
          <div className="login-v2-pass-content">
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <IonInput
                  type={showPasswordNew ? 'text' : 'password'}
                  placeholder="Enter your password"
                  onIonChange={onChange}
                  autocomplete="current-password"
                  id="password-field"
                />
              )}
              rules={{ required: true }}
            />
          </div>
          <div className="login-v2-eye-btn" onClick={() => setShowPasswordNew((v) => !v)}>
            <IonIcon icon={showPasswordNew ? eyeOffOutline : eyeOutline} />
          </div>
        </div>
        {errors.password?.message && (
          <div className="login-v2-field-error">
            <IonIcon icon={warningOutline} />
            <span>{errors.password.message}</span>
          </div>
        )}

        <IonButton type="submit" id="login-submit" expand="block" className="login-v2-submit-btn">
          Log In
        </IonButton>

        <div className="login-v2-meta-row">
          <Link to="/auth/forgotpassword" className="login-v2-forgot-link">
            Forgot password
          </Link>
        </div>
      </form>

      <div className="login-v2-divider" />

      <Link to="/auth/createaccount" className="login-v2-signup-link">
        <button className="login-v2-signup-btn">Sign up</button>
      </Link>

      <p className="login-v2-enterprise">
        Enterprise login?{' '}
        <Link to="/auth/casinoadmin" className="login-v2-enterprise-switch">
          Switch
        </Link>
      </p>
    </div>
  );
};
